import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';

interface SaveSessionModalProps {
  open: boolean;
  subject: string;
  topicName?: string;
  type: 'text' | 'video';
  onSave: (customName: string) => void;
  onSkip: () => void;
}

export default function SaveSessionModal({
  open,
  subject,
  topicName,
  type,
  onSave,
  onSkip,
}: SaveSessionModalProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultSuggestion = topicName
    ? `${topicName} Notes`
    : `${subject} Revision`;

  const [name, setName] = useState(defaultSuggestion);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultSuggestion);
      setIsSaving(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, defaultSuggestion]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsSaving(true);
    setTimeout(() => {
      onSave(trimmed);
      setIsSaving(false);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
  };

  const t = {
    overlay: isDark ? 'bg-black/60' : 'bg-black/40',
    modalBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    modalBorder: isDark ? 'border-white/[0.08]' : 'border-neutral-200',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    inputBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-50',
    inputBorder: isDark ? 'border-white/[0.08]' : 'border-neutral-200',
    inputFocus: isDark ? 'focus:border-white/20 focus:ring-white/10' : 'focus:border-neutral-300 focus:ring-neutral-200',
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={`fixed inset-0 z-[60] flex items-end sm:items-center justify-center ${t.overlay} backdrop-blur-sm`}
          onClick={onSkip}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className={`w-full sm:w-[420px] mx-4 sm:mx-0 ${t.modalBg} border ${t.modalBorder} rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'}`}>
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${type === 'video' ? 'ri-movie-ai-line' : 'ri-file-text-line'} text-xl ${t.textSecondary}`} />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-lg font-bold text-center ${t.textPrimary} mb-1`}>
              Save Study Session
            </h2>
            <p className={`text-sm text-center ${t.textMuted} mb-6 leading-relaxed`}>
              Give this session a name so you can find it later in your history.
            </p>

            {/* Input */}
            <div className="mb-6">
              <label className={`block text-xs font-semibold ${t.textSecondary} uppercase tracking-wider mb-2`}>
                Session Name
              </label>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Math Test Prep"
                maxLength={60}
                className={`w-full px-4 py-3.5 rounded-xl text-sm ${t.textPrimary} ${t.inputBg} border ${t.inputBorder} ${t.inputFocus} focus:outline-none focus:ring-2 transition-all duration-200 placeholder:text-neutral-500/50`}
              />
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  `${subject} Revision`,
                  `${subject} Test Prep`,
                  `${subject} Notes`,
                  topicName || '',
                ]
                  .filter(Boolean)
                  .filter((s, i, arr) => arr.indexOf(s) === i)
                  .slice(0, 3)
                  .map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setName(suggestion)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors cursor-pointer ${
                        isDark
                          ? 'border-white/[0.08] text-neutral-400 hover:bg-white/[0.04]'
                          : 'border-neutral-200 text-neutral-500 hover:bg-neutral-50'
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Pressable
                onClick={onSkip}
                className={`flex-1 py-3.5 rounded-xl text-sm font-semibold ${isDark ? 'bg-white/[0.04] text-white border border-white/[0.08]' : 'bg-white text-neutral-900 border border-neutral-200'} transition-colors`}
                scale={0.97}
              >
                Just View
              </Pressable>
              <Pressable
                onClick={handleSave}
                disabled={!name.trim() || isSaving}
                className={`flex-1 py-3.5 rounded-xl text-sm font-semibold text-white transition-colors ${
                  name.trim() && !isSaving
                    ? 'bg-neutral-900'
                    : 'bg-neutral-300 cursor-not-allowed'
                }`}
                scale={0.97}
              >
                {isSaving ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save Session'
                )}
              </Pressable>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}